import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import ActivateIcon from '@mui/icons-material/CheckCircle';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Search, Add } from '@mui/icons-material';
import {
  Button,
  Tooltip,
  Chip,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
} from '@mui/material';

function UserIndex() {
  useSessionValidation();

  const [data, setData] = useState([]);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemName, setDeleteItemName] = useState('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { isAuthenticated, companyId, userId, logout, token } = useAuth();

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) {
      logout();
      return;
    }
  }, [isAuthenticated, companyId]);

  useEffect(() => {
    fetchtableData();
  }, []);

  const fetchtableData = async () => {
    fetchData('UserApi', `/${companyId}/${companyId}`, token)
      .then((response) => {
        if (response.responseCode == '1111') {
          setData(response.response_data);
          // Store usernames in localStorage for duplicate check in Create page
          const userNames = (response.response_data || []).map(u => u.UserName?.trim().toLowerCase()).filter(Boolean);
          localStorage.setItem('existingUserNames', JSON.stringify(userNames));
        }
      })
      .catch((error) => {
        console.error('Error fetching User Details:', error);
        setError('Failed to fetch users.');
      });
  };

  const handleDelete = async () => {
    deleteData('UserApi', `${deleteItemId}`, token)
      .then(() => {
        fetchtableData();
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
        setSuccess('User deleted successfully!');
      })
      .catch((error) => {
        console.error('Error deleting record:', error);
        setError('Failed to delete user.');
      });
  };

  const handleActivate = (itemId) => {
    activateData('UserApi', `${itemId}`, token)
      .then(() => {
        fetchtableData();
        setSuccess('User activated successfully!');
      })
      .catch(() => {
        setError('Failed to activate user.');
      });
  };

  const openConfirmDialog = (item) => {
    setDeleteItemId(item.Id);
    setDeleteItemName(`${item.FirstName} ${item.LastName}`);
    setConfirmDialogOpen(true);
  };

  const closeConfirmDialog = () => {
    setDeleteItemId(null);
    setConfirmDialogOpen(false);
  };

  const getFilteredData = () => {
    if (!searchTerm) return data;
    return data.filter(item =>
      item.FirstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.LastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.UserName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.Email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filteredData = getFilteredData();

  return (
    <div style={{ minWidth: '100%', background: '#f5f7fa', padding: '10px', position: 'relative' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)', padding: '12px', position: 'relative', color: 'black', fontFamily: 'Times New Romans' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
          <h4 style={{ fontSize: '17px', fontWeight: '700', margin: 0, letterSpacing: '-0.3px', fontFamily: 'Times New Romans', textTransform: 'uppercase', color: 'black' }}>
            User Management
          </h4>
        </div>

        {/* Search and Create Button */}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name, username, email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search style={{ color: '#667eea', fontSize: '20px' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#f8f9fa',
                fontSize: '13px',
                height: '29px',
                '&:hover fieldset': { borderColor: '#667eea' },
                '&.Mui-focused fieldset': { borderColor: '#667eea' },
              },
            }}
          />
          <Tooltip title="Create a new user" arrow>
            <Button
              variant="contained"
              component={Link}
              to="/userCreate"
              sx={{
                background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)',
                color: 'white',
                padding: { xs: '6px', sm: '4px 12px' },
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '700',
                boxShadow: '0 2px 8px rgba(102, 126, 234, 0.4)',
                minHeight: '29px',
                minWidth: { xs: '29px', sm: 'auto' },
                textTransform: 'none',
                letterSpacing: '0.5px',
                whiteSpace: 'nowrap',
                '& .button-text': { display: { xs: 'none', sm: 'inline' } }
              }}
            >
              <Add sx={{ fontSize: '16px' }} />
              <span className="button-text" style={{ marginLeft: '4px' }}>Create User</span>
            </Button>
          </Tooltip>
        </div>

        {/* Table */}
        <div style={{ marginBottom: '10px' }}>
          <TableContainer component={Paper} style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)', borderRadius: '10px', overflow: 'hidden' }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow style={{ background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)', height: '27px' }}>
                  {['Sl No.', 'First Name', 'Last Name', 'User Name', 'Contact Number', 'Email', 'Status', 'Actions'].map((col) => (
                    <TableCell key={col} style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 12px', height: '27px', textAlign: col === 'Actions' ? 'right' : 'left' }}>
                      {col}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.map((item, index) => (
                  <TableRow key={item.Id} style={{ background: index % 2 === 0 ? '#ffffff' : '#f8f9fa', borderBottom: '2px solid #f0f0f0' }}>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{index + 1}</TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{item.FirstName || '-'}</TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{item.LastName || '-'}</TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{item.UserName || '-'}</TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{item.ContactNumber || '-'}</TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{item.Email || '-'}</TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                      <Chip label={item.IsActive ? 'Active' : 'Inactive'} color={item.IsActive ? 'success' : 'default'} size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
                    </TableCell>
                    <TableCell style={{ padding: '8px 12px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <Tooltip title="Edit User" arrow>
                          <Button component={Link} to={`/userEdit/${item.Id}`} size="small" style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>✏️</Button>
                        </Tooltip>
                        <Tooltip title="View Details" arrow>
                          <Button component={Link} to={`/userDetail/${item.Id}`} size="small" style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>👁️</Button>
                        </Tooltip>
                        {item.IsActive ? (
                          <Tooltip title="Delete User" arrow>
                            <Button size="small" onClick={() => openConfirmDialog(item)} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>🗑️</Button>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Activate User" arrow>
                            <Button size="small" onClick={() => handleActivate(item.Id)} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>✅</Button>
                          </Tooltip>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={closeConfirmDialog} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px', padding: '8px' } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span style={{ fontSize: '24px' }}>⚠️</span>
            <Typography variant="h6" component="span" sx={{ fontWeight: 600, fontSize: '16px' }}>Confirm Delete</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '14px', color: '#4a5568' }}>Are you sure you want to delete "{deleteItemName}"?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeConfirmDialog} sx={{ textTransform: 'none', color: '#718096' }}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error" sx={{ textTransform: 'none', borderRadius: '6px' }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!error || !!success} autoHideDuration={6000} onClose={() => { setError(''); setSuccess(''); }} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={() => { setError(''); setSuccess(''); }} severity={error ? 'error' : 'success'}>{error || success}</Alert>
      </Snackbar>
    </div>
  );
}

export default UserIndex;
